import { flattenParts, positionFor } from '@kronos/domain'
import type { AuthoredLine, DesignSource, LinesVerdict, PartsEntry } from '@kronos/domain'
import type { ComponentType } from '../../../data'
import { StructureTree } from '../../tree/StructureTree'
import type { Problems } from '../itemDraft'
import { EditorSection } from './controls'
import { ForkNotice } from './ForkNotice'
import { PartRow } from './PartRow'
import { PartSearch } from './PartSearch'

/**
 * What it is made of: the list, the search that adds to it, and the tree it
 * makes — drawn from the draft, so it changes as the list does.
 *
 * Whether saving edits the current revision or forks the next is not decided
 * here. `ForkNotice` reads the same `mayEditLines` verdict the server will
 * apply in `saveStructure`, and says it before Save is pressed.
 */
export function PartsSection({
  parentId,
  parentName,
  parts,
  onParts,
  saved,
  lock,
  revision,
  problems,
  dirty,
  types,
  source,
  preview,
}: {
  parentId: string | null
  parentName: string
  parts: readonly PartsEntry[]
  onParts: (next: readonly PartsEntry[]) => void
  saved: readonly AuthoredLine[]
  lock: LinesVerdict | null
  revision: { readonly current: string | null; readonly next: string }
  problems: Problems
  dirty: boolean
  types: readonly ComponentType[]
  source: DesignSource
  preview: ComponentType
}): JSX.Element {
  const byId = new Map(types.map((t) => [t.id, t]))
  const fixed = saved.flatMap((l) => (l.position === null ? [] : [l.position]))
  const all = flattenParts(parts).flatMap((l) => (l.position === null ? [] : [l.position]))
  const positionsOf = (e: PartsEntry): readonly string[] => (e.kind === 'slots' ? e.positions : [])

  let offset = 0
  const rows = parts.map((entry, i) => {
    const own = positionsOf(entry)
    const issues = problems.positions?.slice(offset, offset + own.length) ?? []
    offset += own.length
    const taken = parts.flatMap((e, j) => (j === i ? [] : positionsOf(e)))
    return { entry, issues, taken, key: `${entry.kind}:${entry.childTypeId}` }
  })

  const add = (t: ComponentType): void =>
    onParts([
      ...parts,
      t.tracking === 'serialized'
        ? { kind: 'slots', childTypeId: t.id, positions: [positionFor(t.name, all)] }
        : { kind: 'count', childTypeId: t.id, quantity: 1 },
    ])

  return (
    <EditorSection
      title="Made of"
      intro={`Everything that goes into one ${parentName.trim() || 'of these'}. Parts with their own serial get a named position to scan into; counted parts are used up by the build.`}
    >
      <ForkNotice lock={lock} revision={revision} dirty={dirty} />

      {parts.length > 0 ? (
        <ul className="ie-parts">
          {rows.map(({ entry, issues, taken, key }, i) => (
            <PartRow
              key={key}
              entry={entry}
              type={byId.get(entry.childTypeId)}
              issues={issues}
              taken={taken}
              fixed={fixed}
              onChange={(next) => onParts(parts.map((p, j) => (j === i ? next : p)))}
              onRemove={() => onParts(parts.filter((_, j) => j !== i))}
            />
          ))}
        </ul>
      ) : (
        <p className="ie-empty">Nothing on the list yet. Search below and tap what goes into it.</p>
      )}
      {problems.parts ? <p className="ie-field__error">{problems.parts}</p> : null}

      <PartSearch
        types={types}
        source={source}
        parentId={parentId ?? ''}
        assigned={parts.map((p) => p.childTypeId)}
        onAdd={add}
      />

      {parts.length > 0 ? (
        <div className="ie-preview">
          <span className="ie-field__label">Preview — how it’s made</span>
          <StructureTree rootTypeId={preview.id} compact draft={{ type: preview, lines: flattenParts(parts) }} />
        </div>
      ) : null}
    </EditorSection>
  )
}
